import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { friendlyErrorMessage } from '../components/ErrorMessage';
import useStickyToolbar from '../hooks/useStickyToolbar';
import { isMobileOrderViewport } from '../hooks/useIsMobileOrderViewport';
import { TOAST_PHONE_BOTTOM_HOST } from '../constants/overlayLayers';

/**
 * App-wide toast system. `useToast()` exposes success/error/info/dismiss; the
 * provider renders a fixed portal host on document.body (z-[100], above the
 * z-50 modals per house convention). Use instead of hand-rolled successMessage
 * banners and window.alert.
 *
 *   const toast = useToast();
 *   toast.success('Task assigned');
 *   toast.error(err);                 // Error/object → friendlyErrorMessage
 *   toast.error('Custom copy', { sticky: true, action: { label: 'View', to: '/x' } });
 *
 * opts: { sticky?: boolean, duration?: number, action?: { label, to | onClick },
 *         phone?: { bottom?: boolean, duration?: number } }.
 *
 * 📱 `phone` (OPT-IN, owner 2026-10-29 — the order pages' tick-move toast ONLY): below `md` the
 * toast sits at the BOTTOM, above the pinned phone action bar and the nav (`TOAST_PHONE_BOTTOM`) —
 * and above a first visit's cookie banner while it shows (`TOAST_PHONE_BOTTOM_HOST`, R-2) —
 * instead of over the tab strip, and `phone.duration` replaces the window when the push happens on a
 * phone. From `md` up it is an ordinary top toast with its ordinary duration. A call WITHOUT
 * `phone` is untouched: same toast object, same classes, same timer, no bottom host rendered.
 *
 * ── WHY TOP-RIGHT, NOT bottom-4 right-4 (2026-09-24) ──
 * The host used to sit `fixed bottom-4 right-4`, which is the EXACT corner
 * `pages/BudgetCostCodes.js`'s sticky totals bar (`data-save-bar`, also
 * `fixed bottom-4 right-4`) occupies — a toast rendered directly on top of
 * "Cost Code budget … | Labour inc allowances … | Total …" at z-[100] over
 * its z-40. Bottom-right also collides with the mobile bottom nav
 * (`Layout.js`, `fixed bottom-0 … z-50`) on phones. Moved to the top instead.
 *
 * The offset clears TWO things a page may stack under the app bar, using the
 * house's own measured constants rather than a guessed number:
 *   - `useStickyToolbar()`'s `toolbarTop` — the Layout desktop app bar height
 *     (`APP_BAR_LG_PX` = 56, `hooks/useStickyToolbar.js`), 0 below `lg` because
 *     the mobile header there is NOT sticky (it scrolls away with the page).
 *   - one further toolbar-row allowance, so a page that also renders a
 *     `components/ui/Toolbar` (sticky `top-0 lg:top-14`, `py-2` + the shared
 *     `CONTROL_HEIGHT` — `2.375rem` at `md`+, `44px` tap target below it) does
 *     not have its Sort/Filters/layout controls buried under an open toast.
 *     This context has no page-specific toolbar to measure, so the allowance
 *     is a fixed, generous single row rather than a per-page ref.
 * `env(safe-area-inset-top)` clears the iOS notch on top of both. A page with
 * neither a Toolbar nor the desktop app bar (e.g. this one, `BudgetCostCodes`)
 * simply gets a little extra headroom above the toast — never a collision.
 */
const ToastContext = createContext(null);

// Auto-dismiss windows by type; errors linger longer so they can be read.
const DURATIONS = { success: 3000, info: 3000, error: 6000 };

// Bound the visible stack — a burst of failures shouldn't bury the screen.
const MAX_TOASTS = 5;

// Opaque in BOTH themes, deliberately with NO `dark:` variant: `index.css`'s
// global `html.dark .bg-*-50/-100/-200 { … !important }` table rewrites every
// one of those light-token classes to a ~10-25% translucent fill regardless of
// what `dark:bg-*-900/…` companion class sits beside it (that table intercepts
// the LIGHT class, not the dark one) — which is what made the old
// `bg-green-50 dark:bg-green-900/30` pairing render at ~10% opacity in dark
// mode, see-through over whatever sits behind the toast. `-600`/`-700` solid
// shades are NOT in that table (checked against the whole file), so the same
// class renders identically, opaquely, in both themes — no cascade trap to
// re-check the next time that table grows. Contrast of white text on each,
// measured (WCAG relative-luminance formula, not eyeballed): green-700
// ~5.01:1, red-600 ~4.83:1, amber-700 ~5.02:1 — all clear 4.5:1. amber-500/600
// were rejected: ~3.19:1 and worse, same shade of failure already recorded
// against `DueIndicator`'s bg-amber-500 (2.15:1) elsewhere in this codebase.
const TOAST_STYLES = {
  success: 'bg-green-700 border border-green-800 text-white',
  error: 'bg-red-600 border border-red-800 text-white',
  info: 'bg-amber-700 border border-amber-800 text-white',
};

// Clears the Layout desktop app bar (see the docblock above) plus a fixed
// safe-area inset for the iOS notch. Not a Tailwind class: the offset needs
// `useStickyToolbar()`'s live `isLg` value, and mixing a JS-computed pixel
// figure into an arbitrary-value string is how a stale one ships unnoticed.
const TOAST_TOOLBAR_ALLOWANCE_PX = 56; // one generous toolbar row (measured: CONTROL_HEIGHT 38px + Toolbar's py-2 16px ≈ 54px)
const TOAST_GAP_PX = 16;

function toastHostTop(appBarPx) {
  return `calc(${appBarPx + TOAST_TOOLBAR_ALLOWANCE_PX + TOAST_GAP_PX}px + env(safe-area-inset-top, 0px))`;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Deterministic, collision-free ids (no Date.now()/Math.random()).
  const idRef = useRef(0);
  const timersRef = useRef(new Map());
  // Reused, not re-measured: `toolbarTop` is exactly the Layout app-bar
  // allowance (0 below `lg`, 56 at `lg`+) that `ui/Toolbar` itself pins under.
  const { toolbarTop } = useStickyToolbar();

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const push = useCallback((type, message, opts = {}) => {
    const id = (idRef.current += 1);
    const phoneBottom = !!(opts.phone && opts.phone.bottom === true);
    setToasts((prev) => {
      const next = [...prev, { id, type, message, action: opts.action || null, ...(phoneBottom ? { phoneBottom: true } : {}) }];
      // Cap the stack — clear the timer of anything we drop so it can't leak.
      const overflow = next.length - MAX_TOASTS;
      if (overflow > 0) {
        for (const dropped of next.slice(0, overflow)) {
          const timer = timersRef.current.get(dropped.id);
          if (timer) {
            clearTimeout(timer);
            timersRef.current.delete(dropped.id);
          }
        }
        return next.slice(overflow);
      }
      return next;
    });
    if (opts.sticky !== true) {
      const phoneMs = opts.phone && opts.phone.duration > 0 && isMobileOrderViewport() ? opts.phone.duration : null;
      const ms = phoneMs || opts.duration || DURATIONS[type] || 3000;
      const timer = setTimeout(() => dismiss(id), ms);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  const success = useCallback((message, opts) => push('success', message, opts), [push]);
  const info = useCallback((message, opts) => push('info', message, opts), [push]);
  // Accepts a string OR an Error/object — routed through friendlyErrorMessage
  // so the .code mapping from api.js's apiThrow surfaces friendly copy.
  const error = useCallback(
    (messageOrError, opts) => push('error', friendlyErrorMessage(messageOrError), opts),
    [push]
  );

  // Clear any outstanding timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // Memoized so consumers don't re-render on every push/dismiss (the callbacks
  // are already useCallback-stable, so this identity only changes on mount).
  const value = useMemo(() => ({ success, error, info, dismiss }), [success, error, info, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastHost toasts={toasts} onDismiss={dismiss} topPx={toolbarTop} />
    </ToastContext.Provider>
  );
}

function ToastHost({ toasts, onDismiss, topPx }) {
  if (typeof document === 'undefined') return null;

  // 📱 A `phone.bottom` toast renders TWICE, and CSS picks one: the top copy is `max-md:hidden`, the
  // bottom host is `md:hidden` — so a phone sees it at the bottom and a desktop exactly where it was
  // (display:none, so a screen reader hears one). Every other toast keeps its byte-identical classes,
  // and the bottom host is not rendered at all unless such a toast exists.
  const renderToast = (t, placementClass) => (
        <div
          key={t.id}
          role={t.type === 'error' ? 'alert' : 'status'}
          className={`px-4 py-3 rounded-lg shadow text-sm flex items-start gap-3 ${TOAST_STYLES[t.type] || TOAST_STYLES.info}${placementClass}`}
        >
          <div className="flex-1 min-w-0">
            <span>{t.message}</span>
            {t.action && (
              <span className="ml-2">
                {t.action.to ? (
                  <Link
                    to={t.action.to}
                    onClick={() => onDismiss(t.id)}
                    className="font-semibold underline hover:no-underline"
                  >
                    {t.action.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => { t.action.onClick?.(); onDismiss(t.id); }}
                    className="font-semibold underline hover:no-underline"
                  >
                    {t.action.label}
                  </button>
                )}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDismiss(t.id)}
            aria-label="Dismiss"
            className="flex-shrink-0 opacity-70 hover:opacity-100 leading-none"
          >
            ✕
          </button>
        </div>
  );
  const phoneBottom = toasts.filter((t) => t.phoneBottom);

  const host = (
    <>
      <div
        className="fixed right-4 max-md:left-4 z-[100] flex flex-col gap-2 max-w-sm max-md:max-w-none"
        style={{ top: toastHostTop(topPx) }}
      >
        {toasts.map((t) => renderToast(t, t.phoneBottom ? ' max-md:hidden' : ''))}
      </div>
      {phoneBottom.length > 0 && (
        <div
          data-testid="toast-host-phone-bottom"
          className="md:hidden fixed left-4 right-4 z-[100] flex flex-col gap-2"
          style={{ bottom: TOAST_PHONE_BOTTOM_HOST }}
        >
          {phoneBottom.map((t) => renderToast(t, ''))}
        </div>
      )}
    </>
  );

  return createPortal(host, document.body);
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

// Toast-or-no-op (2026-08-15). For PAGES whose test harnesses render them
// without the app shell: `App.js` always mounts `ToastProvider`, so in the
// running app this IS `useToast()` — but dozens of existing page suites render
// `DepartmentOrders`/`MyOrders` bare, and a hook that throws there would
// convert every one of them into a provider-wrapping rewrite. Outside a
// provider each method is an inert no-op.
// 🔴 Do NOT reach for this in a component that can require the provider —
// the throwing `useToast()` stays the default; this exists for the
// harness-compatibility case alone, and a test that wants to SEE the toast
// wraps in `ToastProvider` and uses the real thing.
const NOOP_TOAST = Object.freeze({
  success: () => {},
  error: () => {},
  info: () => {},
  dismiss: () => {},
});

export function useToastOrNoop() {
  const ctx = useContext(ToastContext);
  return ctx || NOOP_TOAST;
}

export default ToastContext;
